import { Link, useRouter, useNavigate } from "@tanstack/react-router";
import {
  Newspaper, Map, Activity, Camera, Users, MessageSquareCheck, Network, Search as SearchIcon,
  Trophy, Bell, Mail, Bookmark, Settings, User, Plus, MoreHorizontal, LogOut, Shield,
  BarChart3, AlertTriangle, MapPin, TrendingUp, Gavel, Briefcase, MessageSquare, Target,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/user-avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { CreatePostDialog } from "@/components/create-post-dialog";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { XamseenLogo, XamseenMark } from "@/components/xamseen-logo";
import { PulseIndicator, usePulse, pulseLevelStyle } from "@/components/pulse-indicator";

type NavItem = { to: string; label: string; icon: typeof Map; badge?: number; tone?: string; hint?: string };


export function AppShell({
  children,
  rightRail,
  allowAnonymous,
}: {
  children: React.ReactNode;
  rightRail?: React.ReactNode;
  /** Shareable, crawlable surfaces (a post, a public profile) opt out of the gate. */
  allowAnonymous?: boolean;
}) {
  const { user, profile, signOut, loading } = useAuth();

  const router = useRouter();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Signed out on a member-only surface: send them to sign-in and remember where
  // they were headed so they land there after authenticating.
  const gated = !allowAnonymous && !loading && !user;
  useEffect(() => {
    if (!gated) return;
    const path = router.state.location.pathname;
    // Never re-gate the account surface itself — that would nest redirect params.
    if (/^\/(signin|signup|auth|forgot-password|reset-password|verify-email)(\/|$)/.test(path)) return;
    const dest = path + router.state.location.searchStr;
    navigate({ to: "/signin", search: { redirect: dest }, replace: true });
  }, [gated, navigate, router.state.location.pathname, router.state.location.searchStr]);


  useEffect(() => {
    if (!user) return;
    supabase.from("notifications").select("id", { count: "exact", head: true }).eq("user_id", user.id).eq("read", false)
      .then(({ count }) => setUnread(count ?? 0));
    const ch = supabase.channel(`notif-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => setUnread((n) => n + 1))
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [user]);


  const username = profile?.username ?? "";

  const isActive = (to: string) => {
    const path = router.state.location.pathname;
    if (to === "/") return path === "/";
    return path === to || path.startsWith(to + "/");
  };

  // Xamseen's own vocabulary — these are the first-class concepts.
  const coreNav: NavItem[] = [
    { to: "/", label: "Lens", icon: Newspaper, tone: "text-plum", hint: "What's happening" },
    { to: "/map", label: "Map", icon: Map, tone: "text-tide", hint: "Issues around you" },
    { to: "/pulse", label: "Pulse", icon: Activity, tone: "text-ember", hint: "Live community signal" },
    { to: "/report", label: "Report", icon: Camera, tone: "text-sun", hint: "Share an issue" },
    { to: "/communities", label: "Spaces", icon: Users, tone: "text-moss", hint: "Your communities" },
    { to: "/questions", label: "Answers", icon: MessageSquareCheck, tone: "text-sun", hint: "Find & give solutions" },
    { to: "/projects", label: "Projects", icon: Network, tone: "text-moss", hint: "Community projects" },
    { to: "/impact", label: "Impact", icon: Target, tone: "text-plum", hint: "Track your impact" },
    { to: "/leaderboard", label: "Leaderboard", icon: Trophy, tone: "text-ember", hint: "Top contributors" },
  ];

  const personalNav: NavItem[] = [
    { to: "/search", label: "Discover people", icon: SearchIcon },
    { to: "/notifications", label: "Notifications", icon: Bell, badge: unread },
    { to: "/messages", label: "Messages", icon: Mail },
    { to: "/bookmarks", label: "Bookmarks", icon: Bookmark },
    { to: user ? `/profile/${username}` : "/signin", label: "Profile", icon: User },
  ];

  const moreNav: NavItem[] = [
    { to: "/nearby", label: "Nearby", icon: MapPin },
    { to: "/discussions", label: "Discussions", icon: MessageSquare },
    { to: "/solutionists", label: "Solutionists", icon: Shield },
    { to: "/emergency", label: "Emergency", icon: AlertTriangle },
    { to: "/trends", label: "Trends", icon: TrendingUp },
    { to: "/dashboard", label: "Community insight", icon: BarChart3 },
    { to: "/workspace", label: "Authority workspace", icon: Briefcase },
    { to: "/accountability", label: "Accountability", icon: Gavel },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  const mobileNav: NavItem[] = [
    { to: "/", label: "Lens", icon: Newspaper },
    { to: "/pulse", label: "Pulse", icon: Activity },
    { to: "/report", label: "Report", icon: Camera },
    { to: "/communities", label: "Spaces", icon: Users },
    { to: user ? `/profile/${username}` : "/signin", label: "You", icon: User },
  ];


  if (gated) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-[1290px] gap-0 px-0 sm:px-4">
        {/* Left rail (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-[88px] shrink-0 flex-col items-center justify-between border-r border-border py-3 md:flex xl:w-[275px] xl:items-stretch xl:px-2">
          <div className="flex w-full flex-col gap-1">
            <Link to="/" aria-label="Xamseen home" className="mx-auto mb-3 flex h-12 items-center justify-center rounded-2xl px-2 hover:bg-accent xl:mx-0 xl:justify-start xl:px-3">
              <XamseenLogo className="h-5 w-auto max-w-[52px] object-contain object-left xl:max-w-none" />
            </Link>


            {coreNav.map((item) => (
              <NavLink key={item.to + item.label} item={item} active={isActive(item.to)} showHint />
            ))}

            <span className="my-2 hidden h-px bg-border xl:block" />

            {personalNav.map((item) => (
              <NavLink key={item.to + item.label} item={item} active={isActive(item.to)} />
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button aria-label="More navigation options" className="mx-auto flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[15px] text-foreground/85 hover:bg-accent xl:mx-0 xl:w-full">
                  <span className="grid h-7 w-7 place-items-center"><MoreHorizontal className="h-5 w-5" /></span>
                  <span className="hidden xl:inline">More</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                {moreNav.map((it) => (
                  <DropdownMenuItem key={it.to} asChild>
                    <Link to={it.to}><it.icon className="mr-2 h-4 w-4" />{it.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user ? (
              <Button onClick={() => setCreateOpen(true)}
                className="mx-auto mt-3 h-12 w-12 rounded-full p-0 text-base font-semibold shadow-sm xl:mt-4 xl:h-12 xl:w-full xl:rounded-2xl">
                <Plus className="h-5 w-5 xl:hidden" />
                <span className="hidden xl:inline">Share something</span>
              </Button>
            ) : (
              <Button asChild className="mx-auto mt-3 h-12 w-12 rounded-full p-0 xl:mt-4 xl:h-12 xl:w-full xl:rounded-2xl">
                <Link to="/signin"><User className="h-5 w-5 xl:hidden" /><span className="hidden xl:inline">Sign in</span></Link>
              </Button>
            )}
          </div>


          {/* Account chip */}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="mx-auto mb-2 flex items-center gap-3 rounded-full p-2 transition-colors hover:bg-accent xl:mx-0 xl:w-full xl:p-3">
                  <UserAvatar
                    name={profile?.full_name}
                    username={profile?.username}
                    avatarUrl={profile?.avatar_url}
                    size={36}
                    className="h-9 w-9"
                  />
                  <div className="hidden min-w-0 flex-1 text-left xl:block">
                    <div className="truncate text-sm font-semibold">{profile?.full_name || profile?.username}</div>
                    <div className="truncate text-xs text-muted-foreground">@{profile?.username}</div>
                  </div>
                  <MoreHorizontal className="hidden h-4 w-4 text-muted-foreground xl:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-60">
                <DropdownMenuLabel>
                  <div className="font-medium">{profile?.full_name || profile?.username}</div>
                  <div className="text-xs text-muted-foreground">{profile?.reputation_points ?? 0} reputation</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile/$username" params={{ username }}><User className="mr-2 h-4 w-4" />My profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/settings"><Settings className="mr-2 h-4 w-4" />Settings</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/admin"><Shield className="mr-2 h-4 w-4" />Moderation</Link></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut}><LogOut className="mr-2 h-4 w-4" />Sign out @{profile?.username}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </aside>

        {/* Main column */}
        <main className="min-w-0 flex-1 border-x border-border md:max-w-[640px]">
          {/* Mobile top bar — safe-area aware, avatar optically centred with the wordmark */}
          <div className="sticky top-0 z-30 border-b border-border bg-background/85 pt-[env(safe-area-inset-top)] backdrop-blur-md md:hidden">
            <div className="flex h-14 items-center justify-between gap-3 px-3 xs:px-4">
              <Link to="/" aria-label="Xamseen home" className="flex h-11 shrink-0 items-center">
                <XamseenLogo className="h-5 w-auto max-w-[132px] object-contain object-left" />
              </Link>
              <div className="flex h-11 shrink-0 items-center justify-end">
                <MobileAccountMenu />
              </div>
            </div>
          </div>



          <div className="px-4 py-4 sm:px-6 md:py-6">{children}</div>
          <div className="h-20 md:hidden" />
        </main>

        {/* Right rail (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-[350px] shrink-0 overflow-y-auto px-6 py-3 lg:block">
          {rightRail ?? <DefaultRightRail />}
        </aside>
      </div>

      {/* Mobile bottom tab bar — designed for thumbs, labelled in Xamseen's language */}
      <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div className="grid grid-cols-5">
          {mobileNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            const isReport = item.to === "/report";
            return (
              <Link key={item.to} to={item.to}
                className={cn("relative flex flex-col items-center gap-1 py-2.5", active ? "text-plum" : "text-muted-foreground")}>
                <span className={cn(
                  "grid h-9 w-9 place-items-center rounded-2xl transition-colors",
                  isReport ? "bg-primary text-primary-foreground" : active ? "bg-plum/15" : ""
                )}>
                  <Icon className={cn("h-5 w-5", active && !isReport && "stroke-[2.4]")} />
                </span>
                <span className={cn("text-[10px] font-medium", active && "font-semibold")}>{item.label}</span>
                {item.badge ? (
                  <span className="absolute right-[24%] top-1 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                ) : null}
              </Link>
            );
          })}

        </div>
      </nav>

      {/* Floating compose (all viewports) */}
      {user && (
        <button onClick={() => setCreateOpen(true)}
          aria-label="Create post"
          className="fixed bottom-20 right-4 z-30 grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground ring-1 ring-primary/20 transition-transform hover:scale-105 active:scale-95 md:bottom-6 md:right-6 xl:hidden">
          <Plus className="h-6 w-6" />
        </button>
      )}

      {user && <CreatePostDialog open={createOpen} onOpenChange={setCreateOpen} />}
    </div>
  );
}

function NavLink({ item, active, showHint }: { item: NavItem; active: boolean; showHint?: boolean }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className={cn(
        "group relative mx-auto flex items-center gap-3 rounded-2xl px-3 py-2.5 transition-colors xl:mx-0 xl:w-full",
        active ? "bg-accent/70 font-semibold text-foreground" : "text-foreground/85 hover:bg-accent/40"
      )}
    >
      {active && <span className="absolute left-0 top-1/2 hidden h-6 w-[3px] -translate-y-1/2 rounded-full bg-plum xl:block" />}
      <span className="relative grid h-7 w-7 shrink-0 place-items-center">
        <Icon className={cn("h-[22px] w-[22px] transition-colors", active ? item.tone ?? "text-plum" : "text-foreground/70 group-hover:text-foreground")} />
        {item.badge ? (
          <span className="absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {item.badge > 9 ? "9+" : item.badge}
          </span>
        ) : null}
      </span>
      <span className="hidden min-w-0 flex-1 xl:block">
        <span className="block truncate text-[15px] leading-tight">{item.label}</span>
        {showHint && item.hint && (
          <span className="block truncate text-[11px] text-muted-foreground">{item.hint}</span>
        )}
      </span>
    </Link>
  );
}




function MobileAccountMenu() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const username = profile?.username ?? "";

  if (!user) {
    return (
      <Button asChild size="icon" variant="ghost" className="h-11 w-11 rounded-full">
        <Link to="/signin"><User className="h-5 w-5" /></Link>
      </Button>
    );
  }

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/signin", replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Account menu"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full transition-colors hover:bg-accent"
        >
          <UserAvatar
            name={profile?.full_name}
            username={profile?.username}
            avatarUrl={profile?.avatar_url}
            size={36}
            className="h-9 w-9 ring-1 ring-border"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="bottom" className="w-56">
        <DropdownMenuLabel>
          <div className="font-medium">{profile?.full_name || profile?.username}</div>
          <div className="text-xs text-muted-foreground">@{username}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/profile/$username" params={{ username }}><User className="mr-2 h-4 w-4" />My profile</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/settings"><Settings className="mr-2 h-4 w-4" />Settings</Link></DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}><LogOut className="mr-2 h-4 w-4" />Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DefaultRightRail() {
  const { profile } = useAuth();
  const pulse = usePulse(profile?.city ?? null);
  const tone = pulseLevelStyle(pulse.level);

  return (
    <div className="space-y-4 py-2">
      <Link to="/pulse" className="panel panel-accent flex items-center gap-3 px-4 py-3.5 transition-colors hover:border-plum/50">
        <PulseIndicator level={pulse.level} intensity={pulse.intensity} size={16} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{pulse.headline}</span>
          <span className={cn("block truncate text-xs", tone.text)}>{pulse.detail}</span>
        </span>
      </Link>

      <Link to="/search" className="flex items-center gap-2 rounded-2xl border border-border bg-surface px-4 py-2.5 text-sm text-muted-foreground hover:border-plum/40">
        <SearchIcon className="h-4 w-4 text-plum" /> Search Xamseen
      </Link>

      <div className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">Around you</h3>
        <p className="mt-1 text-xs text-muted-foreground">Issues, projects and Spaces from your city and region.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <RailChip to="/nearby" label="Nearby" />
          <RailChip to="/map" label="Map" />
          <RailChip to="/communities" label="Spaces" />
        </div>
      </div>

      <div className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">Turn talk into action</h3>
        <p className="mt-1 text-xs text-muted-foreground">Answers become solutions. Solutions become projects.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <RailChip to="/questions" label="Answers" />
          <RailChip to="/projects" label="Projects" />
          <RailChip to="/impact" label="Impact" />
        </div>
      </div>

      <div className="panel px-4 py-4">
        <h3 className="font-display text-[15px] font-semibold">Top contributors</h3>
        <p className="mt-1 text-xs text-muted-foreground">People whose reports and answers keep moving things.</p>
        <Link to="/leaderboard" className="mt-3 inline-flex text-sm font-medium text-plum hover:underline">See the leaderboard →</Link>
      </div>

      <div className="space-y-2 px-2 pb-2">
        <p className="text-xs text-muted-foreground">
          Xamseen · <Link to="/emergency" className="hover:underline">Emergency</Link> · <Link to="/settings" className="hover:underline">Settings</Link>
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          <Link to="/privacy" className="hover:underline">Privacy</Link> ·{" "}
          <Link to="/terms" className="hover:underline">Terms</Link> ·{" "}
          <Link to="/community-guidelines" className="hover:underline">Guidelines</Link> ·{" "}
          <Link to="/data-use" className="hover:underline">Data use</Link> ·{" "}
          <Link to="/cookies" className="hover:underline">Cookies</Link> ·{" "}
          <Link to="/accessibility" className="hover:underline">Accessibility</Link> ·{" "}
          <Link to="/contact" className="hover:underline">Contact</Link>
        </p>
      </div>
    </div>
  );
}

function RailChip({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="rounded-full border border-border px-3 py-1 text-xs font-medium text-foreground/85 transition-colors hover:border-plum/50 hover:text-plum">
      {label}
    </Link>
  );
}

